import type { MillsheetFieldsFragment, MillsheetSortInput, MillsheetSortKeyEnum } from '@mbfab/client-api'
import type { MillsheetsFilter, MillsheetSortConfig } from '@mbfab/shared'
import {
  compareFunction,
  compareMillsheetItemValueByConfig,
  isNull,
  isPresent,
  isSome,
  millsheetsFilterSchema,
  normalizeNumber,
} from '@mbfab/shared'
import dayjs from 'dayjs'
import { sortBy, sum, uniq } from 'es-toolkit'
import { z } from 'zod'

import type { MillsheetSearch } from './millsheets-table'
import type { MillsheetColumnKey, MillsheetItemRow, MillsheetRow } from './types'

import type { DataTableSorter } from '@/components/ui/data-table/data-table'
import { parseFilterByKey } from '@/lib/url-filter-values'

//
// ホーム（ミルシート一覧）の絞り込み・並び替え状態。
// v1 は jotai の millsheetFilterJotai で保持しているが、client は画面コンポーネントに
// GraphQL・グローバル状態を持ち込まない方針のため、props 駆動（route が useState で保持）にする。
// フィルタの形自体は v1 の MillsheetSearchCondition.filter（= shared の MillsheetsFilter）をそのまま使う。
//

export type MillsheetFilterState = MillsheetsFilter

export const EMPTY_MILLSHEET_FILTER: MillsheetFilterState = {}

export const EMPTY_MILLSHEET_SEARCH: MillsheetSearch = { filter: EMPTY_MILLSHEET_FILTER, freeWord: '' }

// URL のクエリから読んだ絞り込み条件の検証（useFilterBarState の parse）。URL は利用者が書き換えられるため、
// キー単位で検証し、型違い・実在しない日付・未知のキーはそのキーだけ落とす（正しい条件は残す）
export const parseMillsheetSearch = (raw: unknown): MillsheetSearch | undefined => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return undefined
  }
  const freeWord = z.string().catch('').parse(Reflect.get(raw, 'freeWord'))
  return { filter: parseFilterByKey(millsheetsFilterSchema, Reflect.get(raw, 'filter')), freeWord }
}

export type MillsheetFilterOptions = {
  manufacturers: string[]
  tradingCompanies: string[]
  customers: string[]
  constructionNames: string[]
  constructionAreas: string[]
  members: string[]
  buildingParts: string[]
  steelTypes: string[]
  steelSectionTypes: string[]
  submissionDates: string[]
  endorsementCompanies: string[]
}

// フィルタ候補（絞り込みフォームの選択肢）を、現在取得済みのミルシート一覧から導出する。
// v1 jotai の millsheetSearchOptionsRelatedSelector の移植
export function buildFilterOptions(millsheets: readonly MillsheetFieldsFragment[]): MillsheetFilterOptions {
  const manufacturers = uniq(millsheets.map((x) => x.manufacturerName))
  const tradingCompanies = uniq(millsheets.map((x) => x.tradingCompany)).filter((x) => x !== '')
  const customers = uniq(millsheets.map((x) => x.customer)).filter((x) => x !== '')
  const constructionNames = uniq(millsheets.map((x) => x.constructionName)).filter((x) => x !== '')
  const items = millsheets.flatMap((x) => x.millsheetItems)
  // 工区・節・部位は明細の属性のため、候補も明細の値から作る（親行の値は集約文字列で候補にならない）
  const constructionAreas = uniq(items.map((x) => x.constructionArea)).filter((x) => x !== '')
  const members = uniq(items.map((x) => x.member)).filter((x) => x !== '')
  const buildingParts = uniq(items.map((x) => x.buildingPart)).filter((x) => x !== '')
  const steelTypes = uniq(items.map((x) => x.steelType))
  const steelSectionTypes = uniq(items.map((x) => x.sectionType))
  const endorsementCompanies = uniq(items.map((x) => x.endorsementCompany).filter(isSome)).filter((x) => x !== '')
  const submissionDates = uniq(
    millsheets
      .map((x) => x.submissionDate)
      .filter((x): x is string => x !== null)
      .map((x) => x.slice(0, 10)),
  )
    .sort()
    .reverse() // 新しい日付を上に

  return {
    manufacturers,
    tradingCompanies,
    customers,
    constructionNames,
    constructionAreas,
    members,
    buildingParts,
    steelTypes,
    steelSectionTypes,
    submissionDates,
    endorsementCompanies,
  }
}

// fetchMillsheets の結果（steelOrderReferenceNumber を追加で持つ MillsheetFieldsFragment）を受け取れるようにする
type MillsheetSource = MillsheetFieldsFragment & { steelOrderReferenceNumber?: string | null }

// バックエンドで絞り込み済みのページを再除外せず、一覧表示用の派生値だけを付与する。
export function toMillsheetRows(millsheets: readonly MillsheetSource[]): MillsheetRow[] {
  return millsheets.map((x) => ({
    ...x,
    steelTypes: uniq(x.millsheetItems.map((item) => item.steelType).filter(isSome)),
    sectionTypes: uniq(x.millsheetItems.map((item) => item.sectionType).filter(isSome)),
    sizes: uniq(x.millsheetItems.map((item) => item.size).filter(isSome)),
    totalWeight: sum(
      x.millsheetItems
        .map((item) => item.weightBeforeProcessing)
        .filter(isSome)
        .map((item) => normalizeNumber(item) ?? 0),
    ),
    millsheetItems: sortBy(
      x.millsheetItems.map((item) => ({ ...item, isMatched: false })),
      [(item) => item.rank],
    ),
  }))
}

// v1 apps/frontend/client/src/routes/t/$tenant_id/index.tsx の filteredMillsheets の移植。
// 差分: v1 は jotai の初期値が undefined（未初期化）の間だけ「絞り込みなし・ハイライトなし」になるが、
// client は props 駆動で常にオブジェクト（既定 {}）を持つため、この分岐は
// 「フィルタが1件も設定されていないか」で判定する（結果は同じ: 何も設定していなければ絞り込みなし・
// ハイライトなしになる。v1 で「フィルタを何か1つでも設定すると、明細条件が空でも全明細がハイライトされる」
// という挙動も含めて忠実に移植している）
export function applyMillsheetFilter(
  millsheets: readonly MillsheetSource[],
  filter: MillsheetFilterState,
): MillsheetRow[] {
  const rows = toMillsheetRows(millsheets)

  const hasAnyFilter = Object.keys(filter).length > 0
  if (!hasAnyFilter) {
    return rows
  }

  const matched = rows
    .filter((millsheet) => {
      return [
        isPresent(filter.manufacturerName?.in) ? filter.manufacturerName.in.includes(millsheet.manufacturerName) : true,
        isPresent(filter.constructionName?.in) ? filter.constructionName.in.includes(millsheet.constructionName) : true,
        isPresent(filter.orderNumber?.contains) ? millsheet.orderNumber.includes(filter.orderNumber.contains) : true,
        isPresent(filter.innerNo?.contains) ? millsheet.innerNo.includes(filter.innerNo.contains) : true,
        isPresent(filter.issuedDate?.gte)
          ? dayjs(millsheet.issuedDate).isAfter(dayjs(filter.issuedDate.gte)) ||
            dayjs(millsheet.issuedDate).isSame(dayjs(filter.issuedDate.gte))
          : true,
        isPresent(filter.issuedDate?.lte)
          ? dayjs(millsheet.issuedDate).isBefore(dayjs(filter.issuedDate.lte)) ||
            dayjs(millsheet.issuedDate).isSame(dayjs(filter.issuedDate.lte))
          : true,
        isPresent(filter.submissionDate?.in)
          ? isNull(millsheet.submissionDate)
            ? filter.submissionDate.in.includes('')
            : filter.submissionDate.in.includes(millsheet.submissionDate.slice(0, 10))
          : true,
        isPresent(filter.no?.contains) ? millsheet.no.includes(filter.no.contains) : true,
        isPresent(filter.steelTypes?.contains) ? millsheet.steelTypes.includes(filter.steelTypes.contains) : true,
        isPresent(filter.sectionTypes?.contains) ? millsheet.sectionTypes.includes(filter.sectionTypes.contains) : true,
        isPresent(filter.totalWeight?.gte) ? millsheet.totalWeight >= filter.totalWeight.gte : true,
        isPresent(filter.totalWeight?.lte) ? millsheet.totalWeight <= filter.totalWeight.lte : true,
        isPresent(filter.tradingCompany?.in) ? filter.tradingCompany.in.includes(millsheet.tradingCompany) : true,
        isPresent(filter.customer?.in) ? filter.customer.in.includes(millsheet.customer) : true,
      ].every((x) => x)
    })
    .map((millsheet) => {
      const items: MillsheetItemRow[] = millsheet.millsheetItems.map((steel) => {
        const isMatched = [
          isPresent(filter.steelHeatNumber?.contains)
            ? steel.heatNumber.includes(filter.steelHeatNumber.contains)
            : true,
          isPresent(filter.steelIndividualNumber?.contains)
            ? steel.individualNumber.includes(filter.steelIndividualNumber.contains)
            : true,
          // 工区・節・部位は明細の属性。バックエンドと同じく明細条件として扱う（配下のいずれかの明細が一致すれば残る）
          isPresent(filter.constructionArea?.in) ? filter.constructionArea.in.includes(steel.constructionArea) : true,
          isPresent(filter.member?.in) ? filter.member.in.includes(steel.member) : true,
          isPresent(filter.buildingPart?.in) ? filter.buildingPart.in.includes(steel.buildingPart) : true,
          isPresent(filter.steelSteelType?.in) ? filter.steelSteelType.in.includes(steel.steelType) : true,
          isPresent(filter.steelSectionType?.in) ? filter.steelSectionType.in.includes(steel.sectionType) : true,
          isPresent(filter.steelSize?.contains) ? steel.size.includes(filter.steelSize.contains) : true,
          isPresent(filter.steelLength?.contains) ? steel.length.includes(filter.steelLength.contains) : true,
          isPresent(filter.steelSteelQuantity?.gte) ? steel.quantity >= filter.steelSteelQuantity.gte : true,
          isPresent(filter.steelSteelQuantity?.lte) ? steel.quantity <= filter.steelSteelQuantity.lte : true,
          isPresent(filter.steelWeight?.gte)
            ? (normalizeNumber(steel.weightBeforeProcessing) ?? 0) >= filter.steelWeight.gte
            : true,
          isPresent(filter.steelWeight?.lte)
            ? (normalizeNumber(steel.weightBeforeProcessing) ?? 0) <= filter.steelWeight.lte
            : true,
          isPresent(filter.steelEndorsementCompany?.in)
            ? isNull(steel.endorsementCompany)
              ? false
              : filter.steelEndorsementCompany.in.includes(steel.endorsementCompany)
            : true,
          isPresent(filter.steelEndorsementDate?.gte)
            ? isSome(steel.endorsementDate) &&
              (dayjs(steel.endorsementDate).isAfter(dayjs(filter.steelEndorsementDate.gte)) ||
                dayjs(steel.endorsementDate).isSame(dayjs(filter.steelEndorsementDate.gte)))
            : true,
          isPresent(filter.steelEndorsementDate?.lte)
            ? isSome(steel.endorsementDate) &&
              (dayjs(steel.endorsementDate).isBefore(dayjs(filter.steelEndorsementDate.lte)) ||
                dayjs(steel.endorsementDate).isSame(dayjs(filter.steelEndorsementDate.lte)))
            : true,
        ].every((x) => x)
        return { ...steel, isMatched }
      })
      return { ...millsheet, millsheetItems: items }
    })

  // 明細条件が1つも指定されていない場合、[].some() は常に false になるため、
  // 明細条件による絞り込みを一切していないにもかかわらず「明細0件のミルシート」が
  // ヘッダー条件（メーカー等）だけの絞り込みでも常に除外されてしまう不具合があった。
  // 明細条件が指定されているときだけ isMatched による除外を行う
  const hasDetailFilter = [
    filter.constructionArea?.in,
    filter.member?.in,
    filter.buildingPart?.in,
    filter.steelHeatNumber?.contains,
    filter.steelIndividualNumber?.contains,
    filter.steelSteelType?.in,
    filter.steelSectionType?.in,
    filter.steelSize?.contains,
    filter.steelLength?.contains,
    filter.steelSteelQuantity?.gte,
    filter.steelSteelQuantity?.lte,
    filter.steelWeight?.gte,
    filter.steelWeight?.lte,
    filter.steelEndorsementCompany?.in,
    filter.steelEndorsementDate?.gte,
    filter.steelEndorsementDate?.lte,
  ].some(isPresent)

  return hasDetailFilter ? matched.filter((x) => x.millsheetItems.some((item) => item.isMatched)) : matched
}

// フリーワード検索（2026/08/26 FB）。取得済み一覧に対するクライアントサイドの横断部分一致で、
// ヘッダー項目（証明書番号・工事名称・メーカー等）と明細項目（製鋼番号・個体番号・規格等）を
// まとめて検索する。大文字小文字は区別しない
export function applyMillsheetFreeWord(rows: MillsheetRow[], freeWord: string): MillsheetRow[] {
  const query = freeWord.trim().toLowerCase()
  if (query === '') {
    return rows
  }
  return rows.filter((millsheet) => {
    const headerValues = [
      millsheet.no,
      millsheet.orderNumber,
      millsheet.innerNo,
      millsheet.constructionName,
      millsheet.manufacturerName,
      millsheet.tradingCompany,
      millsheet.customer,
      millsheet.steelOrderReferenceNumber ?? '',
    ]
    const itemValues = millsheet.millsheetItems.flatMap((item) => [
      item.heatNumber,
      item.individualNumber,
      item.steelType,
      item.sectionType,
      item.size,
      item.constructionArea,
      item.member,
      item.buildingPart,
    ])
    return [...headerValues, ...itemValues].some((value) => value.toLowerCase().includes(query))
  })
}

export type MillsheetSort = { key: MillsheetColumnKey; order: 'asc' | 'desc' }

// ソート状態の遷移（クリックのたびに昇順→降順→解除）は DataTable 共通の nextSorters に委譲したため、
// ここでは持たない（旧 nextMillsheetSorters は削除。ui/data-table/sorters.ts 参照）。
// DataTable の sorters は列 key を汎用の string として保持するため、sortMillsheetRows に渡す直前に
// MillsheetColumnKey へ安全に絞り込む（as any/unknown 禁止のため型ガード関数で判定する。
// order-matching/orders-table.tsx の isSteelOrderSortKey と同じ方式）。
//
// MillsheetColumnKey にはファイル系列・actions などソート対象外の key が混在するため、
// orders-table.tsx と同じ単純な Record<MillsheetColumnKey, true> にはできない（全列を
// 網羅させると「ソート不可の列も書かないとコンパイルが通らない」型になってしまう）。
// 代わりに「ソート対象外の列」を明示リストにし、MillsheetColumnKey からそれを除いた
// 残り（=ソート可能な列）に対して Record<..., true> の網羅チェックをかける。
// これにより、MillsheetColumnKey に新しい列が増えたとき「ソート対象外リストに足す」か
// 「ソートマップに足す」のどちらかをしないとコンパイルエラーになり、新設列を無自覚に
// ソート不可のまま放置する事故を防げる
const MILLSHEET_NON_SORTABLE_KEYS = [
  'fileUrl',
  'attachmentFileUrl',
  'shearingCertificateFileUrl',
  'actions',
] as const satisfies readonly MillsheetColumnKey[]
type MillsheetNonSortableKey = (typeof MILLSHEET_NON_SORTABLE_KEYS)[number]
type MillsheetSortableKey = Exclude<MillsheetColumnKey, MillsheetNonSortableKey>

const MILLSHEET_SORT_KEY_MAP = {
  no: true,
  manufacturerName: true,
  issuedDate: true,
  steelTypes: true,
  sectionTypes: true,
  sizes: true,
  totalWeight: true,
  tradingCompany: true,
  customer: true,
  constructionArea: true,
  member: true,
  buildingPart: true,
  constructionName: true,
  orderNumber: true,
  innerNo: true,
  submissionDate: true,
  steelOrder: true,
} as const satisfies Record<MillsheetSortableKey, true>
const MILLSHEET_SORT_KEYS: ReadonlySet<string> = new Set(Object.keys(MILLSHEET_SORT_KEY_MAP))

function isMillsheetSortKey(key: string): key is MillsheetSortKeyEnum {
  return MILLSHEET_SORT_KEYS.has(key)
}

function normalizeMillsheetSorters(
  sorters: readonly DataTableSorter[],
): Array<{ key: MillsheetSortKeyEnum; order: 'asc' | 'desc' }> {
  return sorters
    .filter((sorter): sorter is DataTableSorter & { key: MillsheetSortKeyEnum } => isMillsheetSortKey(sorter.key))
    .map((sorter) => ({ key: sorter.key, order: sorter.order }))
}

// millsheet-workspace.tsx で DataTableSorter[] から sortMillsheetRows へ渡す前に変換するためのヘルパー
export function toMillsheetSorters(sorters: readonly DataTableSorter[]): MillsheetSort[] {
  return normalizeMillsheetSorters(sorters)
}

export function toMillsheetSortInputs(sorters: readonly DataTableSorter[]): MillsheetSortInput[] {
  return normalizeMillsheetSorters(sorters)
}

// v1 apps/frontend/client/src/routes/t/$tenant_id/index.tsx の sortedFilteredMillsheets（switch 部分）の移植
export function sortMillsheetRows(
  rows: MillsheetRow[],
  sorters: MillsheetSort[],
  sortConfig: MillsheetSortConfig,
): MillsheetRow[] {
  if (sorters.length === 0) {
    return rows
  }
  return [...rows].sort((a, b) => {
    const result = sorters.map((sorter) => {
      const order = sorter.order === 'asc' ? 1 : -1
      switch (sorter.key) {
        case 'manufacturerName': {
          const sortConfigItem = sortConfig.manufacturerName ?? []
          if (sortConfigItem.length > 0) {
            return (
              order *
              compareMillsheetItemValueByConfig(a.manufacturerName, b.manufacturerName, sortConfig, 'manufacturerName')
            )
          }
          return order * compareFunction(a.manufacturerName, b.manufacturerName)
        }
        case 'issuedDate':
          return order * compareFunction(a.issuedDate, b.issuedDate)
        case 'no':
          return order * compareFunction(a.no, b.no)
        case 'steelTypes': {
          const sortConfigItem = sortConfig.steelType ?? []
          if (sortConfigItem.length > 0) {
            return (
              order *
              compareMillsheetItemValueByConfig(a.steelTypes.join(','), b.steelTypes.join(','), sortConfig, 'steelType')
            )
          }
          return order * compareFunction(a.steelTypes, b.steelTypes)
        }
        case 'sectionTypes': {
          const sortConfigItem = sortConfig.sectionType ?? []
          if (sortConfigItem.length > 0) {
            return (
              order *
              compareMillsheetItemValueByConfig(
                a.sectionTypes.join(','),
                b.sectionTypes.join(','),
                sortConfig,
                'sectionType',
              )
            )
          }
          return order * compareFunction(a.sectionTypes, b.sectionTypes)
        }
        case 'sizes':
          return order * compareFunction(a.sizes, b.sizes)
        case 'totalWeight':
          return order * compareFunction(a.totalWeight, b.totalWeight)
        // ファイル系の列はURL自体に順序の意味がないため、紐づくファイル数でソートする
        case 'fileUrl':
          return (
            order *
            compareFunction(
              a.s3Objects.filter((x) => x.resourceType === 'millsheets').length,
              b.s3Objects.filter((x) => x.resourceType === 'millsheets').length,
            )
          )
        case 'attachmentFileUrl':
          return (
            order *
            compareFunction(
              a.s3Objects.filter((x) => x.resourceType === 'millsheetAttachments').length,
              b.s3Objects.filter((x) => x.resourceType === 'millsheetAttachments').length,
            )
          )
        case 'shearingCertificateFileUrl':
          return order * compareFunction(a.shearingCertificates.length, b.shearingCertificates.length)
        case 'actions':
          return 0
        case 'tradingCompany':
          return order * compareFunction(a.tradingCompany, b.tradingCompany)
        case 'customer':
          return order * compareFunction(a.customer, b.customer)
        case 'constructionArea':
          return order * compareFunction(a.constructionArea, b.constructionArea)
        case 'member':
          return order * compareFunction(a.member, b.member)
        case 'buildingPart':
          return order * compareFunction(a.buildingPart, b.buildingPart)
        case 'orderNumber':
          return order * compareFunction(a.orderNumber, b.orderNumber)
        case 'innerNo':
          return order * compareFunction(a.innerNo, b.innerNo)
        case 'submissionDate':
          return order * compareFunction(a.submissionDate ?? '', b.submissionDate ?? '')
        case 'constructionName':
          return order * compareFunction(a.constructionName, b.constructionName)
        case 'steelOrder':
          return order * compareFunction(a.steelOrderReferenceNumber ?? '', b.steelOrderReferenceNumber ?? '')
        default: {
          const exhaustiveCheck: never = sorter.key
          return exhaustiveCheck
        }
      }
    })
    return result.reduce((prev, current) => (prev === 0 ? current : prev), 0)
  })
}
